import React, { useState, useEffect } from "react";
import { IconBrandDiscord } from "@tabler/icons-react";

interface DiscordUser {
  id: any;
  username: string;
  avatar: string;
  discriminator: string;
}

interface SpotifyData {
  song: string;
  artist: string;
  album_art_url: string;
}

interface DiscordStatusData {
  discord_user: DiscordUser;
  discord_status: "online" | "idle" | "dnd" | "offline";
  activities: Array<{
    name: string;
    state?: string;
    details?: string;
  }>;
  listening_to_spotify: boolean;
  spotify: SpotifyData | null;
}

const DiscordStatus: React.FC = () => {
  const [statusData, setStatusData] = useState<DiscordStatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const userId = "277084756221362176"; // Replace with your actual ID

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const response = await fetch(
          `https://api.lanyard.rest/v1/users/${userId}`,
        );
        const data = await response.json();
        setStatusData(data.data);
      } catch (error) {
        console.error("Error fetching Discord status:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 10000); // Poll every 10s

    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string): string => {
    switch (status) {
      case "online":
        return "#31a24c"; // Discord green
      case "idle":
        return "#faa61a"; // Discord yellow
      case "dnd":
        return "#f23645"; // Discord red
      case "offline":
        return "#747f8d"; // Discord gray
      default:
        return "#747f8d";
    }
  };

  const getStatusText = (status: string): string => {
    switch (status) {
      case "online":
        return "Online";
      case "idle":
        return "Idle";
      case "dnd":
        return "Do Not Disturb";
      case "offline":
        return "Offline";
      default:
        return "Unknown";
    }
  };

  if (loading) {
    return (
      <>
        <div className="flex items-center gap-2 mb-3">
          <IconBrandDiscord
            size={20}
            className="text-white dark:text-gray-500"
          />
          <span className="text-sm font-medium text-amber-300 dark:text-gray-300">
            Discord
          </span>
          <div className="w-2 h-2 rounded-full text-white dark:text-gray-500" />
        </div>
        <div className="animate-pulse flex gap-3">
          {/* <div className="w-16 h-16 bg-gray-800 rounded-full"></div> */}
          <div className="flex-1">
            <div className="h-4 bg-amber-800 dark:bg-gray-800 rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-amber-800 dark:bg-gray-800 rounded w-1/2"></div>
          </div>
        </div>
      </>
    );
  }

  if (!statusData) {
    return (
      <>
        <div className="flex items-center gap-2 mb-3">
          <IconBrandDiscord size={20} className="text-gray-500" />
          <span className="text-sm font-medium text-gray-400">Discord</span>
        </div>
        <p className="text-xs text-gray-500">Unable to load Discord status</p>
      </>
    );
  }

  const statusColor = getStatusColor(statusData.discord_status);
  const statusText = getStatusText(statusData.discord_status);
  const isOnline = statusData.discord_status !== "offline";

  return (
    <>
      <div className="flex items-center gap-2 mb-3">
        {isOnline ? (
          <IconBrandDiscord size={20} className="text-indigo-500" />
        ) : (
          <IconBrandDiscord size={20} className="text-gray-500" />
        )}
        <span className="text-sm font-medium text-white">Discord</span>
        {isOnline && (
          <div
            className="w-2 h-2 rounded-full"
            style={{ backgroundColor: statusColor }}
          />
        )}
      </div>
      <div className="flex gap-3 items-start">
        {/* Status Info */}
        <div className="flex-1 min-w-0">
          <p
            className="text-gray-400 text-xs truncate"
            style={{ color: statusColor }}
          >
            {statusText}
          </p>

          {/* Activity/Game Info */}
          {statusData.activities && statusData.activities.length > 0 && (
            <p className="text-gray-500 text-xs mt-1 truncate">
              {statusData.activities[0].name}
              {statusData.activities[0].state &&
                ` - ${statusData.activities[0].state}`}
            </p>
          )}

          {/* Spotify Info */}
          {statusData.listening_to_spotify && statusData.spotify && (
            <p className="text-gray-500 text-xs mt-2 truncate">
              🎵 {statusData.spotify.song} by {statusData.spotify.artist}
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default DiscordStatus;
